import React, { useEffect, useState } from "react";
import protectedApi from "../../services/api";
import { itemTitle } from "../../utils/constant";
import ProgramMatrixSkeleton from "../../shared/Skeleton/AdminSkeleton/ProgramMatrixSkeleton";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import DateFilterComponent from "../../shared/DateFilterComponent/DateFilterforChart";
import MuiTooltipWrapper from "../Tooltip/Tooltip";
import { useMediaQuery, useTheme } from "@mui/material";

export default function ProgramMetrix({ onLoadingChange, isLoading }) {
  const [chartData, setChartData] = useState({});
  const [chartOptions, setChartOptions] = useState({});
  const [loading, setLoading] = useState(true); // For loading state
  const [error, setError] = useState(null); // For error handling
  const [selectedDate, setSelectedDate] = useState(moment());
  const [viewDateMode, setViewDateMode] = React.useState("year");
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isLaptop = useMediaQuery(theme.breakpoints.down("lg"));

  const VIEW_OPTIONS = [
   // { value: "all", label: "All" },
    {
      value: "month",
      label: (
        <MuiTooltipWrapper
          title="View data for a specific month/months"
          placement="right"
        >
          <span>Month</span>
        </MuiTooltipWrapper>
      ),
    },
    {
      value: "year",
      label: (
        <MuiTooltipWrapper
          title="View data for a selected year"
          placement="right"
        >
          <span>Year</span>
        </MuiTooltipWrapper>
      ),
    },
  ];

  const handleDateChange = (date, viewMode, formatted) => {
    const isValidDate = date && !isNaN(new Date(date).getTime());
    if (isValidDate || viewMode === "all") {
      setSelectedDate(date);
    }
    setViewDateMode(viewMode === "all" ? "all" : viewMode);
  };

  const getEventMetricColors = () => ({
    completed: "#64748B",
    ongoing: theme.palette.primary.main,
    upcoming: "#CBD5E1",
  });

  const buildEventMetricDatasets = (data) => {
    const { completed, ongoing, upcoming } = getEventMetricColors();

    return [
      {
        label: "Completed",
        data: data?.completed_programs || [],
        backgroundColor: completed,
        borderRadius: 4,
        maxBarThickness: 14,
      },
      {
        label: "Ongoing",
        data: data?.ongoing || [],
        backgroundColor: ongoing,
        borderRadius: 4,
        maxBarThickness: 14,
      },
      {
        label: "Upcoming",
        data: data?.upcoming_events || [],
        backgroundColor: upcoming,
        borderRadius: 4,
        maxBarThickness: 14,
      },
    ];
  };

  const getLegendTooltip = (label) => {
    const tooltips = {
      Completed: "Completed events for the month",
      Ongoing: "Events currently in progress",
      Upcoming: "Events scheduled for the month",
      [`${itemTitle} Count`]: `Monthly ${itemTitle} count on the platform`,
    };
    return tooltips[label] || "";
  };
  let legendTooltip = document.getElementById("legend-tooltip");
  if (!legendTooltip) {
    legendTooltip = document.createElement("div");
    legendTooltip.id = "legend-tooltip";
    legendTooltip.style.position = "fixed";
    legendTooltip.style.pointerEvents = "none";
    legendTooltip.style.background = "#4a4a4a";
    legendTooltip.style.color = "#fff";
    legendTooltip.style.padding = "6px 12px";
    legendTooltip.style.borderRadius = "4px";
    legendTooltip.style.fontSize = "12px";
    legendTooltip.style.whiteSpace = "nowrap";
    legendTooltip.style.transition = "opacity 0.2s ease";
    legendTooltip.style.opacity = "0";
    legendTooltip.style.zIndex = "9999";
    legendTooltip.style.boxShadow = "0 2px 4px rgba(0,0,0,0.2)";
    document.body.appendChild(legendTooltip);
  }
  ChartJS.register({
    id: "legendTooltip",
    afterEvent(chart, args) {
      const { event } = args;
      const legend = chart.legend;
      const legendItems = legend?.legendItems;

      const canvas = chart.canvas;
      const rect = canvas.getBoundingClientRect();
      const tooltip = document.getElementById("legend-tooltip");

      let found = false;
      for (let i = 0; i < legendItems.length; i++) {
        const box = legend?.legendHitBoxes[i];
        if (
          event.x >= box?.left &&
          event.x <= box?.left + box?.width &&
          event.y >= box?.top &&
          event.y <= box?.top + box?.height
        ) {
          const tooltipText = getLegendTooltip(legendItems[i]?.text);

          tooltip.textContent = tooltipText;
          tooltip.style.left = `${rect?.left + box?.left}px`;
          tooltip.style.top = `${rect?.top + box?.top - 50}px`;
          tooltip.style.opacity = "1";

          canvas.style.cursor = "pointer";
          found = true;
          break;
        }
      }

      if (!found) {
        tooltip.style.opacity = "0";
        canvas.style.cursor = "default";
      }
    },
  });

  useEffect(() => {
    onLoadingChange?.(true);
    // setLoading(true)
    // Fetch data from the API
    const fetchData = async (Type) => {
      try {
        let Url = "";
        if (viewDateMode === "month") {
          Url = `/dashboard/metrics-galance?started_year=${moment(
            selectedDate
          ).year()}&started_month=${moment(selectedDate).month() + 1}&for_week=true`;
        } else if (viewDateMode === "year") {
          Url = `/dashboard/metrics-galance?started_year=${moment(
            selectedDate
          ).year()}`;
        }
        const response = await protectedApi.get(Url);
        const data = response.data;
        const labels = data?.category;
        const { ongoing } = getEventMetricColors();
        const hasEventStatusMetrics =
          Array.isArray(data?.completed_programs) &&
          Array.isArray(data?.ongoing) &&
          Array.isArray(data?.upcoming_events);

        const chartData = {
          labels,
          datasets: hasEventStatusMetrics
            ? buildEventMetricDatasets(data)
            : [
                {
                  label: `${itemTitle} Count`,
                  data: data.total_programs,
                  backgroundColor: ongoing,
                },
              ],
        };

        const chartOptions = {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: {
              ticks: {
                autoSkip: true,
              },
            },
            y: {
              beginAtZero: true,
              ticks: {
                beginAtZero: true,
              },
            },
          },
          plugins: {
            legend: {
              position: "bottom",
            },
            tooltip: {
              enabled: true,
              mode: "index",
              intersect: false,
            },
          },
          interaction: {
            mode: "index",
            intersect: false,
          },
          onClick: (event, elements) => {
            if (elements.length > 0) {
              const element = elements[0];
              const dataIndex = element.index;

              const month =
                viewDateMode === "year"
                  ? chartData.labels[dataIndex]
                  : selectedDate;
              const filterMonth = `${moment(selectedDate).format("YYYY")}-${moment(
                month,
                "MMM"
              ).format("MM")}`;

              navigate(
                `/programs?programView=grid&program_type=field_session&filter_by=month&date=%5B${filterMonth}%5D&from=dashboard`
              );
            }
          },
        };

        // New chart end

        // Update the chart state
        setChartData(chartData);
        setChartOptions(chartOptions);
        setLoading(false); // Data is loaded
        onLoadingChange?.(false);
      } catch (err) {
        console.error("Error fetching data", err);
        setError("Failed to load data");
        setLoading(false);
        onLoadingChange?.(false);
      }
    };

    fetchData(viewDateMode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, viewDateMode]);

  if (loading) {
    return <ProgramMatrixSkeleton />; // Loading state
  }

  if (error) {
    return <div>{error}</div>; // Error state
  }

  return (
    <div>
      <div
        className={`title flex items-start ${
          isMobile || isTablet || isLaptop
            ? "flex-col gap-2"
            : "items-center justify-between"
        } py-2 px-4 border-b-2`}
      >
        <h4 className="whitespace-nowrap">
          <MuiTooltipWrapper
            title={`Monthly breakdown of event activity`}
            className="text-base"
          >
            Event Metrics
          </MuiTooltipWrapper>
        </h4>
        <DateFilterComponent
          isDashboard={true}
          label="Select Date"
          selectLabel="Filter By"
          value={selectedDate}
          onChange={handleDateChange}
          availableViews={VIEW_OPTIONS}
          defaultView={viewDateMode}
          onViewChange={(val) => setViewDateMode(val)}
          containerSx={{
            width: "100%",
            display: "flex",
            justifyContent:
              isMobile || isTablet || isLaptop ? "flex-start" : "flex-end",

            "& > .MuiFormControl-root": {
              marginRight: { xs: "0", sm: "0px !important" },
              width: { xs: "100%", sm: "auto" },
            },
          }}
        />
      </div>
      <div
        className="flex items-center justify-center w-full h-60 mt-5 p-3"
        role="region"
        aria-label="Program Metrics Chart"
      >
        <Bar
          data={chartData}
          options={chartOptions}
          aria-label="Bar chart showing program metrics over time"
        />
      </div>
    </div>
  );
}

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);
